// src/lib/taxonomy.js
// Session 74u — the one mutation path for every governed taxonomy.
//
// This header read "all three ... inventory_categories, departments,
// branch_stations" until Session 78n5 and had been wrong since 78n2, which
// added units without updating it. There are FIVE: inventory_categories,
// departments, branch_stations, units_of_measure and denial_reasons.
// Corrected here rather than left, because a header that undercounts is how a
// future session concludes a taxonomy is ungoverned and builds a second path
// for it. Closes #262 gap (3) and #198's governance half; extended at 78n2
// (#398, units) and 78n5 (#397, denial reasons).
//
// One module rather than three because the mutation sequence is identical
// (write, then let the audit trigger record it) and only the rename step
// differs. Two surfaces consume this — Company Settings for the company-wide
// taxonomies, User Management for branch-local stations — and they must not be
// able to drift apart, which is the duplication #198 exists to prevent.
//
// Every write here is a single statement except rename, which is an rpc()
// because it spans up to five tables. Nothing chains client-side writes.

import { supabase } from './supabase';

// ── Categories ────────────────────────────────────────────────────────────
// A rename is a plain UPDATE: inventory_items.category_id is a real foreign
// key, so nothing carries the category name as text and nothing can orphan.
// That is the difference from departments, and it is why only one of these
// two needs an rpc.

// `code` is SELECTED HERE ON PURPOSE, added at Session 78t, and removing it
// breaks SKU generation silently rather than loudly.
//
// generateSkuForCategory() builds an item's code from the parent category's
// `code` and the child's. Given a category list that omits the column it does
// not fail - it returns an empty string for every row, so the caller proposes
// no SKU and nothing anywhere reports an error. That is exactly the defect
// Session 78s.3 shipped and caught by hand: a migration added `code`, three
// screens each SELECTED it explicitly, this shared helper did not, and a form
// that looked finished proposed nothing while every unit test passed.
//
// The item importer reads categories through this function, so the column
// belongs here rather than in a fourth hand-written select beside the three in
// AssignMissingSkusSheet.js, CatalogPromotionModal.js and InventoryItemDetail.js.
// Additive for the only other caller, Company Settings' category management,
// which ignores it.
export async function fetchCategories() {
  const { data, error } = await supabase
    .from('inventory_categories')
    .select('id, name, parent_id, is_active, code')
    .order('name');
  return { rows: data || [], error };
}

export async function addCategory(name, parentId) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a name first.' } };
  const { error } = await supabase
    .from('inventory_categories')
    .insert({ name: trimmed, parent_id: parentId || null });
  return { error };
}

export async function renameCategory(id, name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a name first.' } };
  const { error } = await supabase
    .from('inventory_categories')
    .update({ name: trimmed })
    .eq('id', id);
  return { error };
}

// Deactivating a parent that still has active children would leave those
// children reachable in a picker under a group header that no longer exists.
// Refused rather than cascaded: a cascade would silently retire subcategories
// the admin never looked at, and items are filed on those, not on the parent.
export async function setCategoryActive(id, isActive, allRows) {
  if (!isActive) {
    const activeChildren = (allRows || []).filter(
      r => r.parent_id === id && r.is_active !== false
    );
    if (activeChildren.length > 0) {
      return {
        error: {
          message:
            `Deactivate its ${activeChildren.length} subcategor${activeChildren.length === 1 ? 'y' : 'ies'} first — ` +
            'items are filed on those, not on the parent.',
        },
      };
    }
  }
  const { error } = await supabase
    .from('inventory_categories')
    .update({ is_active: isActive })
    .eq('id', id);
  return { error };
}

// ── Departments ───────────────────────────────────────────────────────────
// users.department, branch_stations.department, tools.department and
// inspections.department all store the NAME as text, so a rename has to move
// five tables together or it orphans four of them. Locked rule 7.

export async function fetchDepartments({ activeOnly = false } = {}) {
  let query = supabase.from('departments').select('id, name, is_active').order('name');
  if (activeOnly) query = query.eq('is_active', true);
  const { data, error } = await query;
  return { rows: data || [], error };
}

export async function addDepartment(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a name first.' } };
  const { error } = await supabase.from('departments').insert({ name: trimmed });
  return { error };
}

export async function renameDepartment(id, name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a name first.' } };
  const { error } = await supabase.rpc('rename_department', {
    p_department_id: id,
    p_new_name: trimmed,
  });
  return { error };
}

export async function setDepartmentActive(id, isActive) {
  const { error } = await supabase
    .from('departments')
    .update({ is_active: isActive })
    .eq('id', id);
  return { error };
}

// ── Stations and trucks ───────────────────────────────────────────────────
// Branch-local, and unique only within a branch — Dallas and Houston can both
// run a "Truck 1". Every read and write here carries branch_id for that reason.
// A station also carries a department, because UserManagementPage filters the
// station picker by branch AND department before it offers anything.

export async function fetchStations({ activeOnly = false } = {}) {
  let query = supabase
    .from('branch_stations')
    .select('id, name, branch_id, department, is_active')
    .order('name');
  if (activeOnly) query = query.eq('is_active', true);
  const { data, error } = await query;
  return { rows: data || [], error };
}

export async function addStation(name, branchId, department) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a name first.' } };
  if (!branchId) return { error: { message: 'Pick a branch first.' } };
  if (!department) return { error: { message: 'Pick a department first.' } };
  const { error } = await supabase
    .from('branch_stations')
    .insert({ name: trimmed, branch_id: branchId, department });
  return { error };
}

export async function renameStation(id, name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a name first.' } };
  const { error } = await supabase.rpc('rename_station', {
    p_station_id: id,
    p_new_name: trimmed,
  });
  return { error };
}

export async function setStationActive(id, isActive) {
  const { error } = await supabase
    .from('branch_stations')
    .update({ is_active: isActive })
    .eq('id', id);
  return { error };
}

// ── Units of measure ──────────────────────────────────────────────────────
// Session 78n2, #398. The fourth governed taxonomy, and the only one that
// answers two questions instead of one:
//
//   kind 'measure' — a STOCKING unit. "What is one of these?" sheet, gallon.
//                    Lives on inventory_items.uom. UN/CEFACT Rec 20.
//   kind 'package' — a PACKAGE type. "How does the vendor ship it?" case,
//                    pallet. Lives on vendor_items.pack_unit. UN/CEFACT Rec 21.
//   kind 'both'    — genuinely both. Vinyl is stocked by the roll AND ships by
//                    the roll; a box of screws is one unit on a shelf.
//
// A rename is a plain UPDATE with no rpc, and that is the categories case
// rather than the departments one. Both foreign keys carry ON UPDATE CASCADE
// against units_of_measure(name), so renaming 'sheet' moves every catalog item
// and vendor price row in the same statement. Nothing stores the word loose,
// so nothing can orphan — which is also why countUnitUsage below matches
// exactly where countDepartmentUsage has to match case-insensitively.
//
// There is no delete, deliberately. The database refuses one on a unit in use
// (ON DELETE RESTRICT), and retiring is what an executive actually wants: the
// word stops being offered and every record that already carries it stays
// readable. Purchasing may add units but never remove them, and that rule is
// enforced by the foreign key rather than by this module.

export async function fetchUnits({ activeOnly = false, kind = null } = {}) {
  let query = supabase
    .from('units_of_measure')
    .select('id, name, code, kind, is_active, sort_order')
    .order('sort_order')
    .order('name');
  if (activeOnly) query = query.eq('is_active', true);
  // 'both' always qualifies, so a caller asking for one list gets that kind
  // plus the shared words rather than having to know which those are.
  if (kind) query = query.in('kind', [kind, 'both']);
  const { data, error } = await query;
  return { rows: data || [], error };
}

export async function addUnit(name, kind, code) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a name first.' } };
  if (!['measure', 'package', 'both'].includes(kind)) {
    return { error: { message: 'Pick whether this is a stocking unit or a package type.' } };
  }
  const { error } = await supabase.from('units_of_measure').insert({
    name: trimmed,
    kind,
    code: (code || '').trim().toUpperCase() || null,
  });
  return { error };
}

// Name, code and kind move in ONE update rather than three chained ones. They
// are three columns of one row, so splitting them would be the client-side
// write chain locked rule 7 exists to forbid — and a half-applied edit here
// would leave a unit renamed but miscategorised, which the dropdowns read.
//
// Known gap, recorded rather than discovered later: the audit trigger logs name
// and is_active changes only, so editing a code or a kind is NOT written to
// taxonomy_change_log. Both are metadata that nothing reads for correctness
// today. Extending the trigger is a migration, and production is not migrated
// yet — so it rides rather than growing this one.
export async function updateUnit(id, { name, code, kind }) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a name first.' } };
  if (!['measure', 'package', 'both'].includes(kind)) {
    return { error: { message: 'Pick whether this is a stocking unit or a package type.' } };
  }
  const { error } = await supabase
    .from('units_of_measure')
    .update({
      name: trimmed,
      kind,
      code: (code || '').trim().toUpperCase() || null,
    })
    .eq('id', id);
  return { error };
}

export async function setUnitActive(id, isActive) {
  const { error } = await supabase
    .from('units_of_measure')
    .update({ is_active: isActive })
    .eq('id', id);
  return { error };
}

// What retiring a unit would take out of circulation. Shown in the confirm step
// so the consequence is stated before it happens — the same contract
// countDepartmentUsage has. Exact match, not ilike: the foreign keys make the
// stored value identical to the list value by construction, so the drift
// departments has to heal for cannot exist here.
export async function countUnitUsage(unitName) {
  const [items, packUnits, offCatalogPackUnits] = await Promise.all([
    supabase.from('inventory_items').select('id', { count: 'exact', head: true }).eq('uom', unitName),
    supabase.from('vendor_items').select('id', { count: 'exact', head: true }).eq('pack_unit', unitName),
    supabase.from('vendor_items').select('id', { count: 'exact', head: true }).eq('off_catalog_pack_unit', unitName),
  ]);
  return {
    items: items.count || 0,
    vendorPrices: (packUnits.count || 0) + (offCatalogPackUnits.count || 0),
  };
}

// ── Denial reasons ────────────────────────────────────────────────────────
// Session 78n5, #397, gate decision D8. The fifth governed taxonomy, and the
// only one whose rename deliberately does NOT reach the records that use it.
//
// The other four all move their consumers when the word changes — departments
// through an rpc across five tables, units through ON UPDATE CASCADE. This one
// must not, and the reason is the whole design of #397:
//
//   material_requests.denial_reason_id    — the LINK. Makes denials countable.
//   material_requests.denial_reason_label — the wording AT THE MOMENT OF DENIAL.
//
// The label is a snapshot, not a copied key. Rename 'Not budgeted' to 'Over
// budget' next year and every past denial still reads the way the manager wrote
// it, while the id keeps them all countable under the renamed reason. A future
// session will be tempted to "fix" the label into a join and must not — see the
// long argument in db/migrations/20260829_denial_reasons.sql, statement 10.
//
// So there is no rpc here and nothing to cascade. A rename is one UPDATE of one
// row, and that is not a shortcut — it is the point.
//
// No delete, matching the other four. A reason is retired, which stops it being
// offered on new denials while old ones stay readable. Deleting one would blank
// the id on every request that carried it (ON DELETE SET NULL) and lose exactly
// the count that justifies inter-branch transfers, #371.

export async function fetchDenialReasons({ activeOnly = false } = {}) {
  let query = supabase
    .from('denial_reasons')
    .select('id, name, is_active, sort_order')
    .order('sort_order')
    .order('name');
  if (activeOnly) query = query.eq('is_active', true);
  const { data, error } = await query;
  return { rows: data || [], error };
}

export async function addDenialReason(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a reason first.' } };
  // Sorted to the end rather than into the middle. sort_order is an argument in
  // this list — the two stock answers lead and 'Other' trails — but a new
  // reason has no claim on a position, and dropping it above 'Other' would be
  // guessing. An executive can reorder deliberately later; this must not
  // silently rank it.
  const { data: last } = await supabase
    .from('denial_reasons')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrder = ((last && last.sort_order) || 0) + 10;
  const { error } = await supabase
    .from('denial_reasons')
    .insert({ name: trimmed, sort_order: nextOrder });
  return { error };
}

export async function renameDenialReason(id, name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { error: { message: 'Enter a reason first.' } };
  const { error } = await supabase
    .from('denial_reasons')
    .update({ name: trimmed })
    .eq('id', id);
  return { error };
}

export async function setDenialReasonActive(id, isActive) {
  const { error } = await supabase
    .from('denial_reasons')
    .update({ is_active: isActive })
    .eq('id', id);
  return { error };
}

// How many denials each reason accounts for, shown on the row itself rather
// than only in a confirm dialog — gate callout I. Eleven denials for "available
// at another branch" is the business case for #371's transfers, sitting on the
// settings screen where an executive already is.
//
// One exact count per reason rather than one query returning every denied row
// and counting client-side. That is more round trips and it is the right
// trade: head:true returns no rows at all, so this cannot be truncated by
// PostgREST's default limit the way a fetch-and-count would be once D&R has
// more than a thousand denials. Silently handing back a short count is exactly
// the defect #394 exists for, and it is not worth reintroducing here to save
// six requests against a list this short.
export async function countDenialReasonUsage(reasonIds) {
  const ids = reasonIds || [];
  if (ids.length === 0) return {};
  const results = await Promise.all(
    ids.map(id =>
      supabase
        .from('material_requests')
        .select('id', { count: 'exact', head: true })
        .eq('denial_reason_id', id)
    )
  );
  const counts = {};
  ids.forEach((id, i) => {
    // A failed count must not read as zero. Zero is a real and meaningful
    // answer here — "Not used yet" — so a broken query that returned 0 would be
    // indistinguishable from a reason nobody has picked. null means unknown and
    // the row renders nothing at all.
    counts[id] = results[i].error ? null : (results[i].count || 0);
  });
  return counts;
}

// ── Change log ────────────────────────────────────────────────────────────
// The audit trail is written by a trigger, not from here. This read exists so
// it is visible in the app rather than write-only — a log nobody can see is
// logging, not governance.
//
// Session 78n2: units are the fourth taxonomy this reads, and it needed no
// change to carry them — fetchTaxonomyLog('unit') already works, because the
// filter was written against the column rather than against the three names
// that happened to exist in August.

export async function fetchTaxonomyLog(taxonomy, { limit = 8 } = {}) {
  let query = supabase
    .from('taxonomy_change_log')
    .select('id, taxonomy, record_name, action, old_value, new_value, changed_at, users:changed_by ( full_name )')
    .order('changed_at', { ascending: false })
    .limit(limit);
  if (taxonomy) query = query.eq('taxonomy', taxonomy);
  const { data, error } = await query;
  return { rows: data || [], error };
}

// How many rows a department rename is about to rewrite. Shown in the confirm
// step so the consequence is stated before it happens rather than discovered
// afterwards. Counted per table with head:true so no rows come back.
// PostgREST has no case-insensitive equality operator, so these use ilike with
// the LIKE metacharacters escaped -- an unescaped name containing % or _ would
// silently over-count. Case-insensitive because rename_department is: the number
// shown here must equal the number the rename actually moves, or the dialog is
// lying. Production carries `Install` and `INSTALL` today.
function ciEq(value) {
  return String(value).replace(/[\\%_]/g, m => '\\' + m);
}

export async function countDepartmentUsage(departmentName) {
  const match = ciEq(departmentName);
  const [users, stations, tools, inspections] = await Promise.all([
    supabase.from('users').select('id', { count: 'exact', head: true }).ilike('department', match),
    supabase.from('branch_stations').select('id', { count: 'exact', head: true }).ilike('department', match),
    supabase.from('tools').select('id', { count: 'exact', head: true }).ilike('department', match),
    supabase.from('inspections').select('id', { count: 'exact', head: true }).ilike('department', match),
  ]);
  return {
    users: users.count || 0,
    stations: stations.count || 0,
    tools: tools.count || 0,
    inspections: inspections.count || 0,
  };
}

export async function countStationUsage(stationName, branchId) {
  const match = ciEq(stationName);
  const [users, tools, inspections] = await Promise.all([
    supabase.from('users').select('id', { count: 'exact', head: true }).ilike('station_truck', match).eq('branch_id', branchId),
    supabase.from('tools').select('id', { count: 'exact', head: true }).ilike('station_truck', match).eq('branch_id', branchId),
    supabase.from('inspections').select('id', { count: 'exact', head: true }).ilike('station_truck', match).eq('branch_id', branchId),
  ]);
  return {
    users: users.count || 0,
    tools: tools.count || 0,
    inspections: inspections.count || 0,
  };
}
